import React, { useEffect, useRef, useState } from 'react'
import { menuItemClasses } from '@mui/material/MenuItem'
import styled from 'particles/themes/styled'
import Icon from 'particles/Icon'
import { BaseMenuItemProps } from '.'
import { StyledBaseMenuItem } from './style'
import Checkbox, { CheckboxSelectState, sizeMap as checkboxSizeMap } from 'particles/Checkbox'

interface MenuItemProps extends Omit<BaseMenuItemProps, 'onChange'> {
    label: string
    checkbox: CheckboxSelectState
    onChange: React.ChangeEventHandler<HTMLInputElement>
    onHeaderClick?: React.MouseEventHandler<HTMLElement>
}

interface StyledMenuItemProps extends BaseMenuItemProps {
    containerHeight?: number
    open?: boolean
}

const StyledMenuItem = styled(StyledBaseMenuItem)<StyledMenuItemProps>`
    &.${menuItemClasses.root} {
        flex-direction: column;
        align-items: flex-start;
        @media screen and (max-width: 768px) {
            min-height: auto;
        }
        .header-container {
            display: flex;
            padding: ${({ open }) => (open ? '2px 6px 2px 4px' : '4px 6px 4px 4px')};
            align-items: inherited;
            gap: ${({ theme }) => theme.spacing[200]};
            width: 100%;
            align-self: stretch;
            box-sizing: border-box;
            .header-checkbox {
                flex: 1 0 0;
            }
            & .Mui-focusVisible {
                width: inherit;
                height: inherit;
                border: 0;
            }
        }
        .options {
            display: flex;
            padding: 0px 0px 4px 24px;
            flex-direction: column;
            align-items: flex-start;
            gap: ${({ theme }) => theme.spacing[100]};
            align-self: stretch;
            & .Mui-focusVisible {
                width: inherit;
                height: inherit;
                border: 0;
            }
        }
        &.${menuItemClasses.focusVisible} {
            border: 3px solid ${({ theme }) => theme.colors.border.actionFocus};
            background-color: transparent;
            .header-container {
                padding: ${({ open }) => (open ? '0px 3px 0px 1px' : '1px 3px 1px 1px')};
            }
        }
        .caret {
            display: flex;
            padding: 4px 0px;
            align-items: center;
        }
    }
`

const MenuItemCheckboxAccordion: React.FC<MenuItemProps> = ({
    label,
    size,
    isDisabled,
    onChange,
    onHeaderClick,
    checkbox,
    ...props
}) => {
    const [open, setOpen] = useState(false)
    const labelRef = useRef<HTMLInputElement>(null)
    // This will be use to calculate alignItems
    const [offsetHeight, setOffsetHeight] = useState(labelRef.current?.offsetHeight)

    useEffect(() => {
        // Minus 8 for the top and bottom padding, please update this if the padding changes
        setOffsetHeight(labelRef.current?.offsetHeight ? labelRef.current?.offsetHeight - 8 : undefined)
    }, [labelRef.current?.offsetHeight])

    const handleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
        setOpen(!open)
        onHeaderClick?.(e)
    }
    return (
        <StyledMenuItem
            {...props}
            disabled={isDisabled}
            open={open}
            disableRipple
            containerHeight={offsetHeight}
            textScale={checkboxSizeMap[size ?? 's'].textScale}
        >
            <div className="header-container" onClick={handleHeaderClick}>
                <Checkbox
                    ref={labelRef}
                    label={label}
                    size={size}
                    isDisabled={isDisabled}
                    className="header-checkbox"
                    onChange={onChange}
                    onClick={e => e.stopPropagation()}
                    checkbox={checkbox}
                />
                <Icon size="16px" name={open ? 'caretUp' : 'caretDown'} className="caret" />
            </div>
            {open && <div className="options">{props.children}</div>}
        </StyledMenuItem>
    )
}

export default MenuItemCheckboxAccordion
